import { useQuery } from '@tanstack/react-query'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { useTranslation } from 'react-i18next'
import { useSearch, useNavigate } from '@tanstack/react-router'
import { FileText, Loader2, Plus, ExternalLink, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Layout } from '@/components/layout'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useToast } from '@/lib/hooks/useToast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { BobAnalyseCard } from './BobAnalyseCard'
import { BobAnalyseNewDialog } from './BobAnalyseNewDialog'
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select'
import * as SelectPrimitive from '@radix-ui/react-select'
import { cn } from '@/lib/utils'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { formatDateTime, formatEndDateTime } from '@/lib/utils/timeFormat'

export const BobAnalyseListPage = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { activeCrisis } = useCrisisStore()
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as { selectedId?: string } | undefined
  const [selectedBobId, setSelectedBobId] = useState<string>('')
  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const previousBobAnalysesRef = useRef<typeof bobAnalyses>([])
  const exportFunctionRef = useRef<(() => Promise<void>) | null>(null)

  // Permission check
  const canCreateBob = useHasPermission('bob.create')

  // Get all BOB analyses for the active crisis
  const {
    data: bobAnalyses,
    refetch: refetchBobAnalyses,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['bob-analyses', activeCrisis?.id],
    queryFn: async () => {
      const analyses = await bobAnalyseService.listBobAnalyses(activeCrisis?.id)
      return analyses
    },
    enabled: !!activeCrisis,
    // No polling needed - WebSocket provides real-time updates
  })

  // Sort BOB analyses by creation date (oldest first for display order)
  const sortedBobAnalyses = useMemo(
    () =>
      bobAnalyses?.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ) || [],
    [bobAnalyses]
  )

  // Set the selected BOB ID when analyses are loaded
  useEffect(() => {
    if (sortedBobAnalyses && sortedBobAnalyses.length > 0) {
      // If a specific ID was passed via search params, use that
      if (searchParams?.selectedId) {
        const exists = sortedBobAnalyses.find(bob => bob.id === searchParams.selectedId)
        if (exists) {
          setSelectedBobId(searchParams.selectedId)
          return
        }
      }
      // Otherwise, use the last (most recent) one
      if (!selectedBobId) {
        setSelectedBobId(sortedBobAnalyses[sortedBobAnalyses.length - 1].id)
      }
    }
  }, [sortedBobAnalyses, searchParams?.selectedId, selectedBobId])

  // Check for status changes and show toast for failures
  useEffect(() => {
    if (
      previousBobAnalysesRef.current &&
      previousBobAnalysesRef.current.length > 0 &&
      bobAnalyses
    ) {
      bobAnalyses.forEach(currentBob => {
        const previousBob = previousBobAnalysesRef.current?.find(bob => bob.id === currentBob.id)
        if (previousBob && previousBob.status !== 'failed' && currentBob.status === 'failed') {
          toast({
            title: 'Fout bij genereren van BOB-GOGME analyse',
            description:
              'Er is een fout opgetreden bij het verbinden met de AI-service. Probeer het opnieuw.',
            variant: 'destructive',
          })
        }
      })
    }
    previousBobAnalysesRef.current = bobAnalyses
  }, [bobAnalyses, toast])

  // Auto-refetch when there's a processing analysis
  useEffect(() => {
    const selectedAnalysis = sortedBobAnalyses?.find(bob => bob.id === selectedBobId)

    // Keep polling if status is draft, processing, or queued
    if (
      selectedAnalysis &&
      (selectedAnalysis.status === 'processing' ||
        selectedAnalysis.status === 'draft' ||
        selectedAnalysis.status === 'queued')
    ) {
      // Start polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }

      pollIntervalRef.current = setInterval(() => {
        refetchBobAnalyses()
      }, 1500)
    } else {
      // Stop polling if analysis is no longer processing
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [selectedBobId, sortedBobAnalyses, refetchBobAnalyses])

  // Handle export to Word
  const handleExport = async () => {
    if (exportFunctionRef.current) {
      try {
        await exportFunctionRef.current()
      } catch (error) {
        console.error('Error exporting to Word:', error)
        toast({
          title: 'Fout bij exporteren',
          description: 'Er is een fout opgetreden bij het exporteren naar Word.',
          variant: 'destructive',
        })
      }
    }
  }

  // Callback to receive export function from child component
  const handleExportReady = useCallback((exportFn: () => Promise<void>) => {
    exportFunctionRef.current = exportFn
  }, [])

  return (
    <Layout removeBottomPadding={true}>
      {/* Hero Section with Title and Controls */}
      <div className="mx-auto px-4 md:px-6 mt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
              {t('bob.titel')}
            </h1>
            <p className="text-base md:text-xl text-muted-foreground">{t('bob.beschrijving')}</p>
          </div>

          {/* Controls: Version Dropdown + New Analyse Button */}
          {!isLoading && !error && (
            <div className="flex flex-col md:flex-row items-center gap-4 mt-4 md:mt-0 w-full md:w-auto">
              {/* BOB Analyse Versions Dropdown - only shows if analyses exist */}
              {sortedBobAnalyses && sortedBobAnalyses.length > 0 && (
                <Select
                  value={selectedBobId}
                  onValueChange={newId => {
                    setSelectedBobId(newId)
                    // Update URL search params when dropdown changes
                    navigate({
                      to: '/bob',
                      search: { selectedId: newId },
                      replace: true, // Replace current history entry instead of pushing a new one
                    })
                  }}
                >
                  <SelectTrigger className="min-w-[280px] h-12">
                    <SelectValue placeholder="Selecteer versie" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedBobAnalyses.map((bob, index) => {
                      const isSelected = selectedBobId === bob.id
                      // Number from oldest to newest: oldest = 1, newest = highest number
                      const analyseNumber = index + 1
                      const createdDate = new Date(bob.createdAt)
                      const formattedDateTime = formatDateTime(createdDate)
                      const analyseLabel = `Strategie ${analyseNumber} | ${formattedDateTime}`

                      return (
                        <SelectPrimitive.Item
                          key={bob.id}
                          value={bob.id}
                          className={cn(
                            'relative flex w-full cursor-default select-none items-center py-2 pr-3 pl-3 text-sm outline-none rounded-md',
                            isSelected
                              ? 'bg-orange-100 text-orange-900 font-medium'
                              : 'hover:bg-slate-100',
                            'focus:bg-orange-100 focus:text-orange-900'
                          )}
                        >
                          <SelectPrimitive.ItemText>{analyseLabel}</SelectPrimitive.ItemText>
                        </SelectPrimitive.Item>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}

              {/* New Analyse Button - Gradient variant with lg size */}
              {activeCrisis?.status === 'active' && canCreateBob && (
                <Button
                  variant="gradient"
                  size="lg"
                  className="gap-2 flex-shrink-0 w-full md:w-auto"
                  onClick={() => setNewDialogOpen(true)}
                >
                  <Plus className="w-5 h-5 flex-shrink-0" />
                  <span>Nieuwe analyse</span>
                </Button>
              )}
            </div>
          )}

        </div>

        {/* Scan Info Section - Shows if selected analysis is based on a scan */}
        {!isLoading &&
          !error &&
          sortedBobAnalyses &&
          sortedBobAnalyses.length > 0 &&
          (() => {
            const selectedAnalysis = sortedBobAnalyses.find(bob => bob.id === selectedBobId)
            const scan = selectedAnalysis?.scan

            if (scan) {
              // Handle timeframe - dates might be strings or Date objects
              let timeperiodDisplay = ''
              if (scan.timeFrame?.from && scan.timeFrame?.to) {
                const fromDate =
                  scan.timeFrame.from instanceof Date
                    ? scan.timeFrame.from
                    : new Date(scan.timeFrame.from)
                const toDate =
                  scan.timeFrame.to instanceof Date
                    ? scan.timeFrame.to
                    : new Date(scan.timeFrame.to)
                timeperiodDisplay = `${formatDateTime(fromDate)} - ${formatEndDateTime(fromDate, toDate)}`
              }

              const keywords =
                scan.keywords && scan.keywords.length > 0 ? scan.keywords.slice(0, 3) : []

              const hasMore = scan.keywords && scan.keywords.length > 3

              return (
                <div className="mt-3">
                  <button
                    onClick={() => window.open(`/scan/detail/${scan.id}`, '_blank')}
                    className="inline-flex items-center gap-2 text-left text-sm group"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">
                        Gebaseerd op omgevingsbeeld:
                      </span>
                      {timeperiodDisplay && (
                        <span className="text-xs text-muted-foreground">{timeperiodDisplay}</span>
                      )}
                      {keywords.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap">
                          {keywords.map((keyword, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded border border-gray-200 group-hover:bg-gray-200 transition-colors"
                            >
                              {keyword}
                            </span>
                          ))}
                          {hasMore && (
                            <span className="text-gray-500 text-xs py-0.5">
                              +{scan.keywords!.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-blue-600 flex-shrink-0 transition-colors" />
                  </button>

                  {/* Export Button - positioned below scan info on the left */}
                  <div className="mt-2">
                    <Button variant="outline" size="sm" className="gap-2" onClick={handleExport}>
                      <Download className="w-4 h-4" />
                      <span>Exporteren naar Word</span>
                    </Button>
                  </div>
                </div>
              )
            }

            return null
          })()}

        {/* Export Button - shows when no scan info but analysis exists */}
        {!isLoading &&
          !error &&
          sortedBobAnalyses &&
          sortedBobAnalyses.length > 0 &&
          selectedBobId &&
          (() => {
            const selectedAnalysis = sortedBobAnalyses.find(bob => bob.id === selectedBobId)
            const scan = selectedAnalysis?.scan

            // Only show standalone export button if there's no scan info
            if (!scan) {
              return (
                <div className="mt-3">
                  <Button variant="outline" size="sm" className="gap-2" onClick={handleExport}>
                    <Download className="w-4 h-4" />
                    <span>Exporteren naar Word</span>
                  </Button>
                </div>
              )
            }

            return null
          })()}
      </div>

      {/* New Analyse Dialog */}
      <BobAnalyseNewDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        onSuccess={() => {
          refetchBobAnalyses()
        }}
        onAnalyseCreated={bobId => {
          // When a new analyse is created, select it
          setSelectedBobId(bobId)
          // Refetch to get the latest data
          refetchBobAnalyses()
        }}
        existingBobAnalyses={sortedBobAnalyses || []}
      />

      {/* BOB Analyse Interactive Card - Full Width */}
      <div className="mx-auto px-4 py-4 md:px-6 md:py-6">
        {isLoading ? (
          <div className="text-center py-8 md:py-12 bg-white rounded-lg shadow border border-gray-200">
            <Loader2 className="w-16 h-16 text-muted-foreground mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-semibold mb-2">{t('bob.laden')}</h3>
            <p className="text-muted-foreground">{t('bob.ladenBeschrijving')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 md:py-12 bg-white rounded-lg shadow border border-gray-200">
            <FileText className="w-12 h-12 md:w-16 md:h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg md:text-xl font-semibold mb-2">{t('bob.foutBijLaden')}</h3>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              {t('bob.foutBijLadenBeschrijving', { error })}
            </p>
            <Button onClick={() => refetchBobAnalyses()} variant="outline">
              {t('bob.opnieuwProberen')}
            </Button>
          </div>
        ) : sortedBobAnalyses && sortedBobAnalyses.length === 0 ? (
          <div className="text-center py-8 md:py-12 bg-white rounded-lg shadow border border-gray-200">
            <h3 className="text-xl font-semibold mb-2">{t('bob.geenOmgevingsbeelden')}</h3>
            <p className="text-muted-foreground mb-4">
              Er zijn nog geen BOB-GOGME analyses gemaakt. Klik op "Nieuwe Analyse" om te beginnen.
            </p>
          </div>
        ) : (
          <BobAnalyseCard
            bobAnalyses={sortedBobAnalyses}
            onRefetch={refetchBobAnalyses}
            selectedBobId={selectedBobId}
            onExportReady={handleExportReady}
          />
        )}
      </div>
    </Layout>
  )
}
